import i18n from "i18next"
import LanguageDetector from "i18next-browser-languagedetector"
import { initReactI18next } from "react-i18next"

import en from "./locales/en.json"
import ja from "./locales/ja.json"
import ko from "./locales/ko.json"
import zhHans from "./locales/zh-hans.json"
import zhHant from "./locales/zh-hant.json"

export const SupportedLanguage = {
  En: "en",
  Ja: "ja",
  ZhHans: "zh-Hans",
  ZhHant: "zh-Hant",
  Ko: "ko",
} as const

export type SupportedLanguage = (typeof SupportedLanguage)[keyof typeof SupportedLanguage]
export type LocalizedValueMap<T> = {
  ja: T
  en: T
  zhHans: T
  zhHant: T
  ko: T
}

export const defaultNS = "common"
export const resources = {
  [SupportedLanguage.En]: {
    common: en.common,
    auth: en.auth,
    nav: en.nav,
    errors: en.errors,
    tenant: en.tenant,
    admin: en.admin,
    consumer: en.consumer,
    purchase: en.purchase,
  },
  [SupportedLanguage.Ja]: {
    common: ja.common,
    auth: ja.auth,
    nav: ja.nav,
    errors: ja.errors,
    tenant: ja.tenant,
    admin: ja.admin,
    consumer: ja.consumer,
    purchase: ja.purchase,
  },
  [SupportedLanguage.ZhHans]: {
    common: zhHans.common,
    auth: zhHans.auth,
    nav: zhHans.nav,
    errors: zhHans.errors,
    tenant: zhHans.tenant,
    admin: zhHans.admin,
    consumer: zhHans.consumer,
    purchase: zhHans.purchase,
  },
  [SupportedLanguage.ZhHant]: {
    common: zhHant.common,
    auth: zhHant.auth,
    nav: zhHant.nav,
    errors: zhHant.errors,
    tenant: zhHant.tenant,
    admin: zhHant.admin,
    consumer: zhHant.consumer,
    purchase: zhHant.purchase,
  },
  [SupportedLanguage.Ko]: {
    common: ko.common,
    auth: ko.auth,
    nav: ko.nav,
    errors: ko.errors,
    tenant: ko.tenant,
    admin: ko.admin,
    consumer: ko.consumer,
    purchase: ko.purchase,
  },
} as const

export const supportedLanguages = Object.values(SupportedLanguage)

export const localizedValueKeyByLanguage: Record<
  SupportedLanguage,
  keyof LocalizedValueMap<unknown>
> = {
  [SupportedLanguage.En]: "en",
  [SupportedLanguage.Ja]: "ja",
  [SupportedLanguage.ZhHans]: "zhHans",
  [SupportedLanguage.ZhHant]: "zhHant",
  [SupportedLanguage.Ko]: "ko",
}

export interface LanguageLike {
  resolvedLanguage?: string | null
  language?: string | null
}

export const resolveSupportedLanguage = (
  detectedLanguage: string | null | undefined
): SupportedLanguage => {
  if (!detectedLanguage) {
    return SupportedLanguage.Ja
  }

  const normalized = detectedLanguage.toLowerCase()

  if (
    normalized === "zh" ||
    normalized.startsWith("zh-hans") ||
    normalized.startsWith("zh-cn") ||
    normalized.startsWith("zh-sg") ||
    normalized.startsWith("zh-my")
  ) {
    return SupportedLanguage.ZhHans
  }

  if (
    normalized.startsWith("zh-hant") ||
    normalized.startsWith("zh-tw") ||
    normalized.startsWith("zh-hk") ||
    normalized.startsWith("zh-mo")
  ) {
    return SupportedLanguage.ZhHant
  }

  if (normalized.startsWith("ja")) {
    return SupportedLanguage.Ja
  }

  if (normalized.startsWith("ko")) {
    return SupportedLanguage.Ko
  }

  return SupportedLanguage.En
}

export const getSupportedLanguage = (languageLike: LanguageLike): SupportedLanguage =>
  resolveSupportedLanguage(languageLike.resolvedLanguage ?? languageLike.language)

export const pickLocalizedValue = <T>(
  values: LocalizedValueMap<T>,
  language: string | null | undefined | LanguageLike
) => {
  const supportedLanguage =
    typeof language === "string" || language == null
      ? resolveSupportedLanguage(language)
      : getSupportedLanguage(language)

  return values[localizedValueKeyByLanguage[supportedLanguage]]
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    defaultNS,
    fallbackLng: SupportedLanguage.Ja,
    supportedLngs: supportedLanguages,
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    detection: {
      order: ["localStorage", "navigator", "htmlTag"],
      caches: ["localStorage"],
      lookupLocalStorage: "i18nextLng",
      convertDetectedLanguage: resolveSupportedLanguage,
    },
  })

export { i18n }
export { Trans, useTranslation } from "react-i18next"
